import React, { useCallback, useEffect, useState } from 'react'

import { Button, Col, DatePicker, Form, Input, Row } from 'antd'
import moment from 'moment'
import { FormattedMessage, useIntl } from 'react-intl'

import { Accessibility } from 'components/Accessibility'
import { Select } from 'components/Dropdown/Select'
import { Instructions } from 'components/Map/Instructions'
import { RangePicker } from 'components/RangePicker/RangePicker'
import { Swatch } from 'components/Swatch/Swatch'
import { useForecasters } from 'hooks/forecasters'
import { useSpecialProducts } from 'hooks/specialProducts'
import messages from 'services/intl/messageDefinitions'
import { postSpecialProduct } from 'services/SpecialProducts'
import { SpecialProductStore } from 'stores/SpecialProductStore'
import { useDateFormat, useId, useLocale } from 'stores/UserStore'
import { useSendNotification } from 'utils/Notifications'
import { createEmptySpecialProduct, getColourForType, SPECIAL_PRODUCT_TYPES } from 'utils/SpecialProducts'

export const CreateForm = ({
    toggleEdit,
    update,
    initialValues = createEmptySpecialProduct(),
    editing,
    closeDrawer,
    autoUpdate = false,
}) => {
    // External imports
    const intl = useIntl()
    const [form] = Form.useForm()

    // Store data
    const locale = useLocale()
    const userId = useId()
    const draftSpecialProduct = SpecialProductStore.useDraftProduct()
    const updateDraftSpecialProduct = SpecialProductStore.useUpdateDraftProduct()
    const resetDraft = SpecialProductStore.useResetDraftProduct()
    const { mutate } = useSpecialProducts()
    const dateFormat = useDateFormat()
    const { forecasters } = useForecasters()

    // Local state
    const [disabledForm, toggleDisabledForm] = useState(editing && !initialValues.userID)
    const [name, setName] = useState(initialValues.name)
    const [nameTouched, setNameTouched] = useState(false)
    const [issuedExpiry, setIssuedExpiry] = useState([initialValues.issueDateTime, initialValues.expiryDateTime])

    // Notifications
    const sendNotification = useSendNotification()

    // Set the user ID to the current user if it is not set and we're not editing an existing forecast (ie a cloned forecast)
    const formattedUserId = initialValues.userID ? initialValues.userID : editing ? null : userId
    const formattedInitialValues = {
        ...initialValues,
        name: initialValues.name,
        userID: formattedUserId,
        specialProductType: initialValues.specialProductType || 'warning',
        issuedExpiry: [new Date(initialValues.issueDateTime), new Date(initialValues.expiryDateTime)],
        startDate: moment(initialValues.startDate),
    }

    const createNewSpecialProduct = formData => {
        const newSetup = createEmptySpecialProduct(locale)
        newSetup.colour = draftSpecialProduct.colour // TODO have only a single source of truth
        newSetup.status = 'draft'
        newSetup.name = formData.name
        newSetup.userID = formData.userID
        newSetup.issueDateTime = issuedExpiry[0]
        newSetup.expiryDateTime = issuedExpiry[1]
        newSetup.startDate = formData.startDate.toDate()
        newSetup.polygons = draftSpecialProduct.polygons // TODO have only a single source of truth
        newSetup.specialProductType = formData.specialProductType

        apiPost(newSetup, locale)
    }

    const apiPost = useCallback(async (data, locale) => {
        await postSpecialProduct(data, locale)
        mutate()
        resetDraft()
        sendNotification(intl.formatMessage({ ...messages.specialProductSaved }, { name: data.name }))
        closeDrawer()
        form.resetFields()
    }, [])

    const cancelDraft = () => {
        closeDrawer()
        form.resetFields()
    }

    const updateDetails = useCallback(
        changedField => {
            const updateData = {
                key: null,
                value: null,
            }

            switch (changedField.key) {
                case 'name':
                    // wait a couple seconds to let user finish typing
                    const formattedName = changedField.value.trim() // remove trailing or procedding white space
                    if (formattedName.length >= 3 && formattedName.length <= 100) {
                        setName(formattedName)
                        setNameTouched(true)
                    }
                    break
                case 'issuedExpiry':
                    break
                case 'specialProductType':
                    const newData = [
                        { key: 'colour', value: getColourForType(changedField.value) },
                        { key: 'specialProductType', value: changedField.value },
                    ] // TODO: colour change should eventually come from the server
                    update(newData)
                    break
                default:
                    updateData.value = changedField.value
                    updateData.key = changedField.key
            }

            if (updateData.value && updateData.key) {
                update([updateData])
            }
        },
        [update]
    )

    const autoChecker = changeValues => {
        const changedField = Object.keys(changeValues)[0]
        const parsedValue = Object.values(changeValues)[0]
        const updateData = { key: changedField, value: parsedValue }

        if (changedField === 'userID') {
            // Now that there is a forecaster allow for editing other fields
            toggleDisabledForm(false)
        }

        if (changedField === 'specialProductType') {
            if (draftSpecialProduct) {
                updateDraftSpecialProduct({
                    ...draftSpecialProduct,
                    colour: getColourForType(parsedValue),
                })
            }
        }

        if (autoUpdate) {
            updateDetails(updateData)
        }
    }

    const updateIssuedExpired = dates => {
        setIssuedExpiry(dates)
        update([
            {
                key: 'issueDateTime',
                value: dates[0],
            },
            {
                key: 'expiryDateTime',
                value: dates[1],
            },
        ])
    }

    // Prevent multiple PUT request when user is typing
    useEffect(() => {
        const handler = setTimeout(() => {
            if (autoUpdate && nameTouched) {
                const updateData = {
                    key: 'name',
                    value: name,
                }
                update([updateData])
                setNameTouched(false)
            }
        }, 1500)

        return () => {
            clearTimeout(handler)
        }
    }, [autoUpdate, name, nameTouched, update])

    const disabledDate = current => {
        return current && current < moment().startOf('day')
    }

    return (
        <div>
            {!editing ? (
                <h1 style={styles.title}>
                    <FormattedMessage {...messages.createSpecialProduct} />
                </h1>
            ) : (
                <h1 style={styles.title}>
                    <FormattedMessage {...messages.editSpecialProduct} />
                </h1>
            )}
            <Instructions />
            <Form
                form={form}
                layout="horizontal"
                onFinish={createNewSpecialProduct}
                autoComplete="on"
                onValuesChange={autoChecker}
                initialValues={formattedInitialValues}
            >
                <Form.Item
                    label={<FormattedMessage {...messages.specialProductName} />}
                    labelCol={{ span: 24 }}
                    name="name"
                    rules={[
                        { required: true, message: <FormattedMessage {...messages.nameRequired} /> },
                        { min: 3, message: <FormattedMessage {...messages.nameTooShort} /> },
                        { max: 100, message: <FormattedMessage {...messages.nameTooLong} /> },
                    ]}
                >
                    <Input disabled={disabledForm} />
                </Form.Item>
                <Row gutter={16}>
                    <Col className="gutter-row" span={12}>
                        <Form.Item
                            label={<FormattedMessage {...messages.forecaster} />}
                            labelCol={{ span: 24 }}
                            name="userID"
                            rules={[{ required: true }]}
                        >
                            <Select data-test={'forecaster'}>
                                {forecasters?.map((item, i) => {
                                    return (
                                        <Select.Option key={i} value={item.id}>
                                            {item.name}
                                        </Select.Option>
                                    )
                                })}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col className="gutter-row" span={12}>
                        <Form.Item
                            rules={[{ required: true }]}
                            label={<FormattedMessage {...messages.specialProductType} />}
                            labelCol={{ span: 24 }}
                            name="specialProductType"
                        >
                            <Select disabled={disabledForm}>
                                {SPECIAL_PRODUCT_TYPES.map((type, i) => {
                                    const standardA11yComponent = <Swatch colour={type.colour} />

                                    return (
                                        <Select.Option key={i} value={type.value}>
                                            <Accessibility standardA11yComponent={standardA11yComponent} />
                                            <FormattedMessage {...messages[type.label]} />
                                        </Select.Option>
                                    )
                                })}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item
                    label={<FormattedMessage {...messages.issueAndExpiry} />}
                    labelCol={{ span: 24 }}
                    name="issuedExpiry"
                    rules={[{ required: true, message: <FormattedMessage {...messages.forecastValidationDate} /> }]}
                >
                    <RangePicker
                        initValues={formattedInitialValues.issuedExpiry}
                        onChange={updateIssuedExpired}
                        disabled={disabledForm}
                    />
                </Form.Item>
                <Row>
                    <Col span={12}>
                        <Form.Item
                            label={<FormattedMessage {...messages.startDateTime} />}
                            labelCol={{ span: 24 }}
                            name="startDate"
                            rules={[
                                { required: true, message: <FormattedMessage {...messages.forecastValidationDate} /> },
                            ]}
                        >
                            <DatePicker
                                disabledDate={disabledDate}
                                showTime
                                minuteStep={15}
                                format={dateFormat + '   HH:mm'}
                                value={formattedInitialValues.startDate}
                                allowClear={false}
                                disabled={disabledForm}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            {editing ? (
                <div style={styles.buttonContainer}>
                    <Button type="primary" onClick={toggleEdit} data-test={'closeEditingButton'}>
                        <FormattedMessage {...messages.closeEditing} />
                    </Button>
                </div>
            ) : (
                <div style={styles.buttonContainer}>
                    <Button onClick={cancelDraft} style={styles.cancelButton}>
                        <FormattedMessage {...messages.cancel} />
                    </Button>
                    <Button type="primary" onClick={() => form.submit()} data-test={'confirmCreate'}>
                        <FormattedMessage {...messages.create} />
                    </Button>
                </div>
            )}
        </div>
    )
}

const styles = {
    button: {
        margin: '0 var(--s0) 0 0',
        color: '#fff',
    },
    closeEditing: {
        float: 'right',
        marginBottom: 'var(--s1)',
    },
    container: {
        display: 'flex',
    },
    cancelButton: {
        margin: '0 var(--s0) 0 0',
        color: '#fff',
        background: 'var(--grey)',
        borderColor: 'var(--grey)',
    },
    deleteButton: {
        marginTop: 'var(--s1)',
        marginLeft: 'auto',
        position: 'fixed',
        bottom: 'var(--s0)',
        right: 'var(--s0)',
    },
    buttonContainer: {
        display: 'flex',
        alignItems: 'right',
        justifyContent: 'right',
    },
    errorMessage: {
        fontSize: '14px',
        marginTop: '-25px',
        color: 'var(--red)',
        fontWeight: '300',
    },
    hide: {
        display: 'none',
    },
    show: {
        display: 'block',
    },
    aspectRose: {
        marginBottom: 'var(--s0)',
    },
    sudoLabel: {
        fontSize: '14px',
    },
    title: {
        fontWeight: 300,
    },
}
